/** @format */

import React, { useState, useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Tabs, Table, Alert } from "antd";
import { BreadCrumb } from "../../../components";
import { findValuesFromArray, getArrUnique } from "../../../utils/helper";
import InputSearch from "../../../components/InputSearch";
import FilterMadrasahUpload from "../../Laporan/Component/FilterMadrasahUpload";
import {
  filterByRoleKabkotaOrProv,
  getNamePenerimaBOS,
  getSumberDanaTahap,
} from "../../../utils/helper/helperBos";
import { useJadwalPengajuanBos } from "hooks/HookBos/useJadwalPengajuanBos";
import { ColumnsTableListMadrasahVerifikasi } from "./ColumnTable";
import { useFormModal } from "hooks/useFormModal";
import { ModalListDokumenBos } from "./ModalListDokumenBos";
import { useVerificator } from "hooks/HookBos/useVerificator";
import { useDebounce } from "hooks/useDebounce";
import notifAlert from "components/NotifAlert";

const LaporanDokumen = () => {
  const itemBreadcrumb = [
    { path: "/", breadcrumbName: "Home" },
    { path: "/", breadcrumbName: "Pencairan Dokumen BOS" },
    { path: "/", breadcrumbName: "Kelengkapan Dokumen BOS" },
  ];

  const {
    auth,
    provinsi,
    kabkota,
    sumberDana,
    filePengajuanBos,
    jadwalPengajuanBos,
    bos,
  } = useSelector(
    (state: any) => ({
      auth: state.auth?.data || null,
      provinsi: state.store?.provinsi,
      kabkota: state.store?.kabkota,
      sumberDana: state.store?.sumberDana || [],
      filePengajuanBos: state.bos?.filePengajuanBos || [],
      jadwalPengajuanBos: state.bos?.jadwalPengajuanBos || [],
      bos: state.bos,
    }),
    shallowEqual,
  );

  const tmpJenisPenerimaBOS = getNamePenerimaBOS();
  const [tmpFilter, setTmpFilter] = useState<any>(null);
  const [search, setSearch] = useState<any>(null);
  const debouncedSearch = useDebounce(search, 400);
  const { open, initialData, openWithInitialData, closeModal } = useFormModal();
  const listSumberJadwal = useMemo(
    () => getArrUnique(jadwalPengajuanBos, "sumberdana"),
    [jadwalPengajuanBos],
  );
  const [activeTabKey, setActiveTabKey] = useState<string>(
    listSumberJadwal[0] || null,
  );

  const { madrasah, listMadrasahAlokasi, loading, confirmDocument } =
    useVerificator({
      auth,
      activeTabKey,
    });

  const { jadwalList, activeJadwal, messageInformation } =
    useJadwalPengajuanBos(bos, activeTabKey, "verifikator", sumberDana) as any;

  const itemsTab: any = [...listSumberJadwal].map((item, i) => {
    return {
      label: findValuesFromArray(sumberDana, "kode", item, "nama"),
      key: `${item}`,
    };
  });

  const handleChangeValues = (values: any) => {
    setTmpFilter(values);
  };

  const dataTable = useMemo(() => {
    let data = madrasah;

    // filter role
    data = filterByRoleKabkotaOrProv(auth?.group_role, data);

    // filter form
    if (tmpFilter?.penerimaBos?.length) {
      data = data.filter((item: any) =>
        tmpFilter.penerimaBos.includes(item.penerimaBos),
      );
    }

    if (tmpFilter?.status !== undefined) {
      data = data.filter(
        (item: any) => item.statusValidasi === tmpFilter.status,
      );
    }

    if (tmpFilter?.kabkota && tmpFilter?.kabkota !== undefined) {
      data = data.filter((item: any) => item.kodeKabkota === tmpFilter.kabkota);
    }

    // search
    if (debouncedSearch) {
      const val = debouncedSearch.toLowerCase();

      data = data.filter((item: any) => {
        return (
          item.namaMadrasah?.toLowerCase().includes(val) ||
          item.nsmMadrasah?.toLowerCase().includes(val)
        );
      });
    }

    // tahap sumber dana
    if (activeTabKey) {
      data = data.filter(
        (item: any) => item.tahap === getSumberDanaTahap(activeTabKey),
      );
    }

    return data;
  }, [madrasah, auth?.group_role, tmpFilter, debouncedSearch, activeTabKey]);

  let listMadrasahNoFilter: any = filterByRoleKabkotaOrProv(
    auth?.group_role,
    madrasah,
  );

  const handleAfterDokumen = useCallback(
    (record: any) => {
      openWithInitialData(record);
    },
    [openWithInitialData],
  );

  const handleValidationDocument = useCallback(
    async (payload: any, type: string) => {
      const res = await confirmDocument(payload, type);
      if (res) {
        const desc = `Data Dokumen Madrasah ${res?.namaMadrasah} telah ${(type === "approve" && "dikirim ke Pusat") || "Ditolak"}`;
        notifAlert({
          type: "success",
          description: desc,
        });
        closeModal();
      }
    },
    [closeModal, confirmDocument],
  );

  const columns = useMemo(() => {
    return ColumnsTableListMadrasahVerifikasi(
      provinsi,
      kabkota,
      tmpJenisPenerimaBOS,
      handleAfterDokumen,
    );
  }, [handleAfterDokumen, kabkota, provinsi, tmpJenisPenerimaBOS]);

  return (
    <>
      <BreadCrumb
        routes={itemBreadcrumb}
        title="Kelengkapan Dokumen BOS"
        footer={
          <Tabs
            defaultActiveKey={activeTabKey}
            size="small"
            onChange={(key) => {
              setTmpFilter(null);
              setActiveTabKey(key);
            }}
            items={itemsTab}
          />
        }
      />
      {activeJadwal && (
        <div className="px-4 mt-2">
          <Alert
            message={
              <div className="p-2">
                <div className="font-bold">{messageInformation}</div>
              </div>
            }
            type="success"
            showIcon
          />
        </div>
      )}

      <FilterMadrasahUpload
        type={activeTabKey}
        jadwalPengajuan={jadwalList}
        listMadrasahAlokasi={listMadrasahAlokasi}
        dataMadrasah={listMadrasahNoFilter}
        loading={loading}
        handleChangeValues={handleChangeValues}
      />
      <div className="m-5 p-5 bg-white shadow-md rounded">
        <div className="mb-4 flex justify-end">
          <InputSearch
            className="w-3/4"
            onChange={(e: any) => setSearch(e.currentTarget.value)}
          />
        </div>
        <Table
          rowKey={(record) => record.nsmMadrasah}
          loading={loading}
          columns={columns}
          dataSource={dataTable}
          bordered
        />
      </div>

      <ModalListDokumenBos
        openModalData={open}
        dataMadrasah={initialData}
        listJadwal={jadwalList}
        activeJadwal={activeJadwal}
        jadwalPengajuanBos={jadwalPengajuanBos}
        auth={auth}
        tmpSumberDana={sumberDana}
        activeTabKey={activeTabKey}
        dataTableDokumen={filePengajuanBos}
        messageInformation={messageInformation}
        handleValidation={handleValidationDocument}
        handleClose={closeModal}
      />
    </>
  );
};

export default LaporanDokumen;
